import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Search,
    ChevronLeft,
    ChevronRight,
    Loader2,
    AlertTriangle,
    FileText,
    Cpu,
    Tag,
    UserCircle,
    Calendar,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
} from 'lucide-react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import './LlmUsagePage.css';
import { useLlmUsage, useLlmUsageFilterOptions, useLlmUsageTimeseries, useJobFilterOptions } from '../hooks/useApi';
import MultiSelectFilter from '../components/MultiSelectFilter';

const ITEMS_PER_PAGE = 20;

const getPageNumbers = (current: number, total: number): (number | '...')[] => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (current > 3) pages.push('...');
    for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
    if (current < total - 2) pages.push('...');
    pages.push(total);
    return pages;
};

const formatDateForInput = (iso: string | undefined): string => (iso ? iso.slice(0, 10) : '');

interface SortableThProps {
    field: string;
    label: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSort: (field: string) => void;
}

const SortableTh: React.FC<SortableThProps> = ({ field, label, sortBy, sortOrder, onSort }) => {
    const isActive = sortBy === field;
    return (
        <th className="llm-usage-sortable-th" onClick={() => onSort(field)}>
            <span className="llm-usage-th-content">
                {label}
                {isActive ? (
                    sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                ) : (
                    <ArrowUpDown size={12} className="llm-usage-sort-icon-idle" />
                )}
            </span>
        </th>
    );
};

const LlmUsagePage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [createdAfter, setCreatedAfter] = useState(searchParams.get('after') || '');
    const [createdBefore, setCreatedBefore] = useState(searchParams.get('before') || '');
    const [selectedGuidelines, setSelectedGuidelines] = useState<string[]>(
        (searchParams.get('guidelines') || '').split(',').filter(Boolean)
    );
    const [selectedModels, setSelectedModels] = useState<string[]>(
        (searchParams.get('models') || '').split(',').filter(Boolean)
    );
    const [selectedRuleTypes, setSelectedRuleTypes] = useState<string[]>(
        (searchParams.get('ruleTypes') || '').split(',').filter(Boolean)
    );
    const [selectedCreators, setSelectedCreators] = useState<string[]>(
        (searchParams.get('createdBy') || '').split(',').filter(Boolean)
    );
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
    const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
        searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc'
    );

    const toggleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    // Keep URL in sync so filters/page survive a refresh.
    useEffect(() => {
        const params: Record<string, string> = {};
        if (search) params.search = search;
        if (createdAfter) params.after = createdAfter;
        if (createdBefore) params.before = createdBefore;
        if (selectedGuidelines.length) params.guidelines = selectedGuidelines.join(',');
        if (selectedModels.length) params.models = selectedModels.join(',');
        if (selectedRuleTypes.length) params.ruleTypes = selectedRuleTypes.join(',');
        if (selectedCreators.length) params.createdBy = selectedCreators.join(',');
        if (currentPage > 1) params.page = currentPage.toString();
        if (sortBy !== 'created_at') params.sortBy = sortBy;
        if (sortOrder !== 'desc') params.sortOrder = sortOrder;
        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, createdAfter, createdBefore, selectedGuidelines, selectedModels, selectedRuleTypes, selectedCreators, currentPage, sortBy, sortOrder]);

    // Reset to page 1 whenever a filter or sort column (not the page itself) changes.
    const filterFingerprint = useMemo(
        () => JSON.stringify({ search, createdAfter, createdBefore, selectedGuidelines, selectedModels, selectedRuleTypes, selectedCreators, sortBy, sortOrder }),
        [search, createdAfter, createdBefore, selectedGuidelines, selectedModels, selectedRuleTypes, selectedCreators, sortBy, sortOrder]
    );
    const prevFingerprintRef = useRef(filterFingerprint);
    useEffect(() => {
        if (prevFingerprintRef.current !== filterFingerprint) {
            prevFingerprintRef.current = filterFingerprint;
            setCurrentPage(1);
        }
    }, [filterFingerprint]);

    const baseParams = useMemo(
        () => ({
            guidelineId: selectedGuidelines.length ? selectedGuidelines.join(',') : undefined,
            models: selectedModels.length ? selectedModels : undefined,
            ruleTypes: selectedRuleTypes.length ? selectedRuleTypes : undefined,
            createdBy: selectedCreators.length ? selectedCreators : undefined,
            createdAfter: createdAfter || undefined,
            createdBefore: createdBefore || undefined,
            search: search || undefined,
        }),
        [selectedGuidelines, selectedModels, selectedRuleTypes, selectedCreators, createdAfter, createdBefore, search]
    );

    const { data: listData, loading: listLoading, error: listError } = useLlmUsage({
        ...baseParams,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        sortBy,
        sortOrder,
    });
    const { data: timeseriesData, loading: timeseriesLoading } = useLlmUsageTimeseries('day', baseParams);
    const { data: filterOptionsData, loading: filterOptionsLoading } = useLlmUsageFilterOptions();
    const { data: jobFilterOptionsData } = useJobFilterOptions();

    const items = listData?.items || [];
    const pagination = listData?.pagination || null;
    const chartPoints = timeseriesData?.points || [];

    const guidelineOptions = useMemo(() => {
        const guidelines = (jobFilterOptionsData as any)?.data?.guidelines || [];
        return guidelines.map((g: any) => ({ label: g.name || g.id, value: g.id }));
    }, [jobFilterOptionsData]);

    const guidelineNameById = useMemo(() => {
        const map = new Map<string, string>();
        guidelineOptions.forEach((g: { label: string; value: string }) => map.set(g.value, g.label));
        return map;
    }, [guidelineOptions]);

    const creatorOptions = useMemo(() => {
        const creators = (jobFilterOptionsData as any)?.data?.creators || [];
        return creators.map((c: any) => ({
            label: c.display_name || c.name || c.username || c.email || c.id,
            value: c.id,
        }));
    }, [jobFilterOptionsData]);

    return (
        <div className="page-container">
            <div className="page-header">
                <h1 className="page-title">LLM使用状況</h1>
                <p className="page-description">
                    LLM呼び出しごとのトークン使用量・キャッシュ状況を時系列・条件で確認します。
                </p>
            </div>

            <div className="llm-usage-chart-section">
                {timeseriesLoading ? (
                    <div className="loading-state">
                        <Loader2 size={20} className="animate-spin" />
                        <span>読み込み中...</span>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={chartPoints} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                            <YAxis
                                yAxisId="tokens"
                                tick={{ fontSize: 11 }}
                                label={{ value: 'トークン数', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                            />
                            <YAxis
                                yAxisId="requests"
                                orientation="right"
                                tick={{ fontSize: 11 }}
                                label={{ value: 'リクエスト数', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                            />
                            <Tooltip />
                            <Legend />
                            <Line yAxisId="tokens" type="monotone" dataKey="prompt_tokens" name="入力トークン" stroke="#3b82f6" dot={false} />
                            <Line yAxisId="tokens" type="monotone" dataKey="completion_tokens" name="出力トークン" stroke="#f59e0b" dot={false} />
                            <Line yAxisId="tokens" type="monotone" dataKey="cached_tokens" name="キャッシュトークン" stroke="#22c55e" dot={false} />
                            <Line yAxisId="requests" type="monotone" dataKey="call_count" name="リクエスト数" stroke="#a855f7" dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>

            <div className="filters-section">
                <div className="filters-row">
                    <div className="search-box">
                        <Search size={20} className="search-icon" />
                        <input
                            type="text"
                            placeholder="URLを検索..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <div className="filter-controls">
                        <div className="multi-select-filter llm-usage-date-filter">
                            <label className="filter-label">
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Calendar size={12} />
                                    期間
                                </span>
                            </label>
                            <div className="llm-usage-date-range">
                                <input
                                    type="date"
                                    value={formatDateForInput(createdAfter)}
                                    onChange={(e) => setCreatedAfter(e.target.value)}
                                    className="llm-usage-date-input"
                                />
                                <span className="llm-usage-date-separator">〜</span>
                                <input
                                    type="date"
                                    value={formatDateForInput(createdBefore)}
                                    onChange={(e) => setCreatedBefore(e.target.value)}
                                    className="llm-usage-date-input"
                                />
                            </div>
                        </div>

                        <MultiSelectFilter
                            label={<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FileText size={12} />ガイドライン</span>}
                            options={guidelineOptions}
                            selectedValues={selectedGuidelines}
                            onSelectionChange={setSelectedGuidelines}
                            placeholder="すべてのガイドライン"
                            loading={filterOptionsLoading}
                        />

                        <MultiSelectFilter
                            label={<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Cpu size={12} />モデル</span>}
                            options={filterOptionsData?.models || []}
                            selectedValues={selectedModels}
                            onSelectionChange={setSelectedModels}
                            placeholder="すべてのモデル"
                            loading={filterOptionsLoading}
                        />

                        <MultiSelectFilter
                            label={<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Tag size={12} />ルール種別</span>}
                            options={filterOptionsData?.rule_types || []}
                            selectedValues={selectedRuleTypes}
                            onSelectionChange={setSelectedRuleTypes}
                            placeholder="すべてのルール種別"
                            loading={filterOptionsLoading}
                        />

                        <MultiSelectFilter
                            label={<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UserCircle size={12} />作成者</span>}
                            options={creatorOptions}
                            selectedValues={selectedCreators}
                            onSelectionChange={setSelectedCreators}
                            placeholder="すべての作成者"
                        />
                    </div>
                </div>
            </div>

            <div className="llm-usage-results">
                {listError && (
                    <div className="error-state">
                        <AlertTriangle size={20} />
                        <span>{listError}</span>
                    </div>
                )}

                {listLoading ? (
                    <div className="loading-state">
                        <Loader2 size={20} className="animate-spin" />
                        <span>読み込み中...</span>
                    </div>
                ) : (
                    <>
                        <div className="llm-usage-table-wrap">
                            <table className="llm-usage-table">
                                <thead>
                                    <tr>
                                        <SortableTh field="created_at" label="作成日時" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="guideline_id" label="ガイドライン" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="violation_category_id" label="カテゴリ" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="rule_type" label="ルール種別" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="model" label="モデル" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="prompt_tokens" label="入力トークン" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="completion_tokens" label="出力トークン" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="cached_tokens" label="キャッシュトークン" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="total_tokens" label="合計トークン" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="html_tokens" label="HTMLトークン" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="rule_prompt_tokens" label="ガイドラインプロンプト" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="url" label="URL" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((row) => (
                                        <tr
                                            key={row.id}
                                            className="llm-usage-row"
                                            onClick={() => navigate(`/llm-usage/${row.id}`)}
                                        >
                                            <td>{new Date(row.created_at).toLocaleString('ja-JP')}</td>
                                            <td>{row.guideline_id ? guidelineNameById.get(row.guideline_id) || row.guideline_id : '-'}</td>
                                            <td>{row.violation_category_id || '-'}</td>
                                            <td>{row.rule_type || '-'}</td>
                                            <td>{row.model}</td>
                                            <td>{row.prompt_tokens.toLocaleString()}</td>
                                            <td>{row.completion_tokens.toLocaleString()}</td>
                                            <td>{row.cached_tokens.toLocaleString()}</td>
                                            <td>{row.total_tokens.toLocaleString()}</td>
                                            <td>{row.html_tokens != null ? row.html_tokens.toLocaleString() : '-'}</td>
                                            <td>{row.rule_prompt_tokens != null ? row.rule_prompt_tokens.toLocaleString() : '-'}</td>
                                            <td className="llm-usage-url-cell" title={row.url || ''}>{row.url || '-'}</td>
                                        </tr>
                                    ))}
                                    {items.length === 0 && (
                                        <tr>
                                            <td colSpan={12} className="llm-usage-empty">該当するデータがありません</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {pagination && pagination.total_pages > 1 && (
                            <div className="pagination">
                                <div className="pagination-info">
                                    {pagination.total_items}件中 {((pagination.current_page - 1) * pagination.items_per_page) + 1}
                                    -{Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)}件を表示
                                </div>
                                <div className="pagination-controls">
                                    <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                        disabled={!pagination.has_previous}
                                    >
                                        <ChevronLeft size={16} />前へ
                                    </button>
                                    <div className="pagination-numbers">
                                        {getPageNumbers(pagination.current_page, pagination.total_pages).map((p, idx) =>
                                            p === '...' ? (
                                                <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                                            ) : (
                                                <button
                                                    key={p}
                                                    className={`pagination-number ${pagination.current_page === p ? 'active' : ''}`}
                                                    onClick={() => setCurrentPage(p)}
                                                >
                                                    {p}
                                                </button>
                                            )
                                        )}
                                    </div>
                                    <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage((p) => Math.min(p + 1, pagination.total_pages))}
                                        disabled={!pagination.has_next}
                                    >
                                        次へ<ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default LlmUsagePage;
